import { useEffect, useState } from 'react';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import {
  Control,
  useFieldArray,
  UseFormRegister,
} from 'react-hook-form';

import { v4 as uuidv4 } from 'uuid';
import config from '../../../config';
import { encrypt } from '../../../utils/encryption';

import {
  type InquiryForm,
  type FormItem,
  type InquiryItem,
  type Smtp,
  UserMailTemplate,
  LayoutItem,
} from '../../../models/Inquiry/Inquiry';

import { getFormItems, getSmtps, getMailTemplates } from '../../../services/inquiry';

import FormItems from './FormItems';
import FormContent from './FormContent';

// COMPONENTS
import SpecificTabs from './SpecificTabs';
import OshiraseTabContents, { OshiraseTabs } from './OshiraseTabContents';
import WebDbTabContents, { webDbTabs } from './WebDbTabContents';

interface Props {
  register: UseFormRegister<InquiryForm>
  control: Control<InquiryForm, any>
  disabled: boolean
  currentTab?: 'webdb' | 'oshirase',
  formValues?: InquiryForm
  getFormValues?: () => InquiryForm
}

const SELECTED_DROPPABLE = 'selected_droppable';
const ITEMS_DROPPABLE = 'items_droppable';

export default function FormDefinition({
  register,
  control,
  disabled,
  currentTab: parentCurrentTab = 'webdb',
  formValues,
  getFormValues,
}: Props): JSX.Element {
  const [items, setItems] = useState<FormItem[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [userSmtps, setUserSmtps] = useState<Smtp[]>([]);
  const [userTemplates, setUserTemplates] = useState<UserMailTemplate[]>([]);

  const {
    fields, remove, move, insert,
  } = useFieldArray({
    control,
    name: 'definition',
  });

  const {
    fields: listLayoutPanel0,
    remove: removeListLayoutPanel0,
    insert: insertListLayoutPanel0,
    move: moveListLayoutPanel0,
  } = useFieldArray({
    control,
    name: 'list_layout.0',
  });

  const {
    fields: listLayoutPanel1,
    remove: removeListLayoutPanel1,
    insert: insertListLayoutPanel1,
    move: moveListLayoutPanel1,
  } = useFieldArray({
    control,
    name: 'list_layout.1',
  });

  const {
    fields: detaislLayoutPanel0,
    remove: removeDetailsLayoutPanel0,
    insert: insertDetailsLayoutPanel0,
    move: moveDetailsLayoutPanel0,
  } = useFieldArray({
    control,
    name: 'details_layout.0',
  });

  const {
    fields: detaislLayoutPanel1,
    remove: removeDetailsLayoutPanel1,
    insert: insertDetailsLayoutPanel1,
    move: moveDetailsLayoutPanel1,
  } = useFieldArray({
    control,
    name: 'details_layout.1',
  });

  useEffect(() => {
    const loadInfo = async (): Promise<void> => {
      getFormItems()
        .then((res) => {
          if (res?.data?.data) {
            // Transform form item (add uuid for D&D purpose)
            setItems(res.data.data?.map((item) => ({
              ...item,
              uuid: uuidv4(),
            })));
          }
        })
        .catch((err) => {
          console.warn(err);
        });

      getSmtps()
        .then((res) => {
          if (res?.data?.data) setUserSmtps(res.data.data);
        })
        .catch((err) => {
          console.warn(err);
        });

      getMailTemplates()
        .then((res) => {
          if (res?.data?.data) setUserTemplates(res.data.data);
        }).catch((err) => {
          console.warn(err);
        });
    };

    // Init of UseEffect
    setIsLoading(true);

    loadInfo().finally(() => {
      setIsLoading(false);
    });
  }, []);

  const onDragEnd = (result: DropResult): void => {
    const { source, destination, draggableId } = result;

    // Dropped outside a Droppable area - remove item if from layout lists
    if (!destination) {
      if (source.droppableId.includes('webdb-list-viewitems-')) {
        const sourceParts = source.droppableId.split('-');
        const sourceType = sourceParts[sourceParts.length - 2];
        const sourceColumnIndex = parseInt(sourceParts[sourceParts.length - 1], 10);

        // Remove item from the appropriate list
        switch (sourceColumnIndex) {
          case 0:
            if (sourceType === 'list') removeListLayoutPanel0(source.index);
            else removeDetailsLayoutPanel0(source.index);
            break;
          case 1:
            if (sourceType === 'list') removeListLayoutPanel1(source.index);
            else removeDetailsLayoutPanel1(source.index);
            break;
          default:
            console.warn(`Unknown column index: ${sourceColumnIndex}`);
        }
      }
      return;
    }

    // SIDE LOGIC: handle items added in ＜項目一覧＞ ============================================
    if ((destination.droppableId.includes('webdb-list-viewitems-'))) {
      const droppableParts = destination.droppableId.split('-');
      const destinationType = droppableParts[droppableParts.length - 2];
      const columnIndex = parseInt(droppableParts[droppableParts.length - 1], 10);

      // Reorder items in the same list
      if (source.droppableId === destination.droppableId) {
        switch (columnIndex) {
          case 0:
            if (destinationType === 'list') moveListLayoutPanel0(source.index, destination.index);
            else moveDetailsLayoutPanel0(source.index, destination.index);
            break;
          case 1:
            if (destinationType === 'list') moveListLayoutPanel1(source.index, destination.index);
            else moveDetailsLayoutPanel1(source.index, destination.index);
            break;
          default:
            console.warn(`Unknown column index: ${columnIndex}`);
            return;
        }

        return;
      }

      // Move items from one list to another
      if (source.droppableId.includes('webdb-list-viewitems-')) {
        // Get the source type and column index from the source droppableId
        const sourceParts = source.droppableId.split('-');
        const sourceType = sourceParts[sourceParts.length - 2];
        const sourceColumnIndex = parseInt(sourceParts[sourceParts.length - 1], 10);

        // Remove item from source panel and insert into destination panel
        let itemToMove;
        switch (sourceColumnIndex) {
          case 0:
            if (sourceType === 'list') {
              itemToMove = listLayoutPanel0[source.index];
              removeListLayoutPanel0(source.index);
            } else {
              itemToMove = detaislLayoutPanel0[source.index];
              removeDetailsLayoutPanel0(source.index);
            }
            break;
          case 1:
            if (sourceType === 'list') {
              itemToMove = listLayoutPanel1[source.index];
              removeListLayoutPanel1(source.index);
            } else {
              itemToMove = detaislLayoutPanel1[source.index];
              removeDetailsLayoutPanel1(source.index);
            }
            break;
          default:
            console.warn(`Unknown source column index: ${sourceColumnIndex}`);
            return;
        }

        if (itemToMove) {
          if (columnIndex === 0) {
            if (destinationType === 'list') insertListLayoutPanel0(destination.index, itemToMove);
            else insertDetailsLayoutPanel0(destination.index, itemToMove);
          }

          if (columnIndex === 1) {
            if (destinationType === 'list') insertListLayoutPanel1(destination.index, itemToMove);
            else insertDetailsLayoutPanel1(destination.index, itemToMove);
          }
        }

        return;
      }

      // Field being used in form definition
      const foundField = fields?.find((field) => field.uuid === draggableId);
      if (!foundField) {
        if (source.droppableId === 'webdb-list-layout-items') {
          // If it's the last item, we can add a blank item
          const newLayoutItem: LayoutItem = {
            label: '空白',
            uuid: `empty_${uuidv4()}`,
          };

          if (columnIndex === 0) {
            if (destinationType === 'list') insertListLayoutPanel0(destination.index, newLayoutItem);
            else insertDetailsLayoutPanel0(destination.index, newLayoutItem);
          }

          if (columnIndex === 1) {
            if (destinationType === 'list') insertListLayoutPanel1(destination.index, newLayoutItem);
            else insertDetailsLayoutPanel1(destination.index, newLayoutItem);
          }

          return;
        }
      }

      const { label, uuid } = foundField;
      const newLayoutItem: LayoutItem = { label, uuid };

      // Insert new item into the appropriate list based on destination droppableId
      if (columnIndex === 0) {
        if (destinationType === 'list') {
          insertListLayoutPanel0(destination.index, newLayoutItem);
        } else {
          insertDetailsLayoutPanel0(destination.index, newLayoutItem);
        }
      }

      if (columnIndex === 1) {
        if (destinationType === 'list') insertListLayoutPanel1(destination.index, newLayoutItem);
        else insertDetailsLayoutPanel1(destination.index, newLayoutItem);
      }
    }

    // MAIN LOGIC: handle items added in ＜表示項目＞ ============================================
    // Reorder items if inside SELECTED_DROPPABLE
    if (source.droppableId === destination.droppableId && source.droppableId === SELECTED_DROPPABLE) {
      move(source.index, destination.index);
    }

    // Move items if coming from ITEMS_DROPPABLE and going to SELECTED_DROPPABLE
    if (source.droppableId === ITEMS_DROPPABLE && destination.droppableId === SELECTED_DROPPABLE) {
      const item = items[source.index];
      if (!item) return;
      const {
        uuid,
        id,
        label,
        placeholder,
        type,
        max_length,
        options,
        prefix,
        suffix,
        children,
      } = item;

      // Format form item to form definition item
      const insertItem: InquiryItem = {
        uuid,
        item_id: id as number,
        label,
        placeholder,
        type: Number(type),
        max_length,
        options,
        required: false,
        prefix,
        suffix,
        children: children?.map((child) => ({
          ...child,
          item_id: child.id as number,
          type: Number(child.type),
          required: false,
        })),
      };

      // Insert new item inside selection at index
      insert(destination.index, insertItem);

      // Update source item uuid (prevent id collision in selection list)
      setItems((currentItems) => {
        const newItems = Array.from(currentItems);
        newItems[source.index] = {
          ...item,
          uuid: uuidv4(),
        };
        return newItems;
      });
    }
  };

  const handleRemoveItem = (index: number): void => {
    remove(index);
  };

  useEffect(() => {
    setCurrentTab(0);
  }, [parentCurrentTab]);

  const navigateToPreview = (): void => {
    // Force form values update before navigating
    setTimeout(() => {
      // Use getFormValues if available to get updated values
      const data = getFormValues ? getFormValues() : formValues;
      if (data) {
        // Use updated form values, including the required checkbox state
        const previewData = {
          ...data,
        };
        window.sessionStorage.setItem(config.preview.storageKey, encrypt(JSON.stringify(previewData)));
        window.open('/inquiry-management/preview', '_blank');
      }
    }, 100);
  };

  const sharedForm = () => (
    <div className="tab-pane active" role="tabpanel">
      <div className="row" style={{ minHeight: 300 }}>
        <div className="col-8 d-flex flex-column" style={{ alignSelf: 'stretch' }}>
          <FormContent
            droppableId={SELECTED_DROPPABLE}
            fields={fields}
            control={control}
            register={register}
            onRemove={handleRemoveItem}
            disabled={disabled}
            sideContent={(
              <button type="button" className="btn btn-primary text-sm px-3" onClick={navigateToPreview}>
                プレビュー表示
                <i className="ms-1 fa-solid fa-up-right-from-square fa-fw" />
              </button>
            )}
          />
        </div>
        <div className="col-4">
          <FormItems
            droppableId={ITEMS_DROPPABLE}
            data={items}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      {/* tabs for parentTabs */}
      <div className="mt-3 nav nav-tabs" role="tablist">
        <div className="nav-item">
          <a
            role="tab"
            className={`nav-link ${currentTab === 0 ? 'active' : ''}`}
            tabIndex={0}
            href="#"
            onClick={() => setCurrentTab(0)}
          >
            表示項目設定
          </a>
        </div>

        {parentCurrentTab === 'webdb' && (
          <SpecificTabs
            tabs={webDbTabs}
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
          />
        )}

        {parentCurrentTab === 'oshirase' && (
          <SpecificTabs
            tabs={OshiraseTabs}
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
          />
        )}
      </div>

      {/* content for parentTabs */}
      <div className="card card-body tab-content">
        {currentTab === 0 && (
          <div className="tab-pane active" role="tabpanel">
            {sharedForm()}
          </div>
        )}

        {/* Pass layout fields from formValues */}
        {parentCurrentTab === 'webdb' && (
          <WebDbTabContents
            items={items}
            formFields={fields}
            formValues={formValues}
            listFields={[listLayoutPanel0, listLayoutPanel1]}
            detailFields={[detaislLayoutPanel0, detaislLayoutPanel1]}
            control={control}
            register={register}
            currentTab={currentTab}
          />
        )}

        {parentCurrentTab === 'oshirase' && (
          <OshiraseTabContents
            smtps={userSmtps}
            templates={userTemplates}
            register={register}
            control={control}
            currentTab={currentTab}
          />
        )}
      </div>
    </DragDropContext>
  );
}
